import { z } from "zod";

export const DatatableStateSchema = z.object({
  showColumnHeaders: z.boolean(),
  sectionColumnId: z.string().nullable().optional(),
  subSectionColumnId: z.string().nullable().optional(),
  globalFilter: z.string(),
  columnFilters: z.array(
    z.object({
      id: z.string(),
      value: z.unknown().transform((value) => {
        // (1) Ensure no __typename properties are included to enable comparison
        // (2) Ensure output type is still unknown (not Record<string, unknown>) to avoid type inference issues
        if (typeof value === "object" && value !== null && !Array.isArray(value)) {
          const cleanedValue: Record<string, unknown> = {};
          for (const key in value as Record<string, unknown>) {
            if (!key.startsWith("__")) {
              cleanedValue[key] = (value as Record<string, unknown>)[key];
            }
          }
          return cleanedValue as unknown;
        }
        return value;
      }),
    }),
  ),
  columnOrder: z.array(z.string()),
  columnVisibility: z.record(z.string(), z.boolean()),
  sorting: z.array(
    z.object({
      id: z.string(),
      desc: z.boolean(),
    }),
  ),
  expanded: z.union([z.literal(true), z.record(z.string(), z.boolean())]),
});

export type DatatableState = z.infer<typeof DatatableStateSchema>;

export function compareDatatableStates(state1: DatatableState, state2?: DatatableState | null | undefined): boolean {
  // we need this because (a) keys in JSON.stringify are not sorted and (b) keys data from the api might have additional __typename properties
  if (!state2) {
    return false;
  }

  // Parse and clean both states using Zod
  const cleanedState1 = DatatableStateSchema.parse(state1);
  const cleanedState2 = DatatableStateSchema.parse(state2);

  // Compare the cleaned states
  return JSON.stringify(cleanedState1) === JSON.stringify(cleanedState2);
}
